import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";

const BUSINESS_TYPES = [
  "Hotel", "Restaurant", "Tea Cafe", "Tea Stall", "Hospital",
  "Corporate Cafeteria", "Catering Service", "Hostel", "Retail Store",
  "Supermarket", "Wholesale Distributor", "Individual / Home", "Other",
];

const PRODUCTS = [
  "Hotel Grade Tea Powder", "Premium Tea Powder", "Elaichi Tea Powder",
  "Masala Tea Powder", "Chocolate Tea Powder", "Not sure yet",
];

// fields: array of field keys to include
export default function LeadForm({
  type = "contact",
  fields = ["name", "phone", "email", "business_type", "message"],
  submitLabel = "Submit Enquiry",
  onSuccess,
  compact = false,
}) {
  const [form, setForm] = useState({});
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (fields.includes("phone") && !form.phone) {
      toast.error("Please enter your phone number");
      return;
    }
    if (!privacyConsent) {
      toast.error("Please agree to the Privacy Policy before submitting your enquiry.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        type,
        ...form,
        privacy_consent: true,
        privacy_consent_at: new Date().toISOString(),
        privacy_policy_version: "1.0",
        marketing_consent: marketingConsent,
        marketing_consent_at: marketingConsent ? new Date().toISOString() : null,
      };
      await api.post("/leads", payload);
      setDone(true);
      toast.success("Enquiry sent! Our team will contact you shortly.");
      onSuccess && onSuccess(form);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="lead-success">
        <CheckCircle2 className="mx-auto text-olive mb-4" size={56} />
        <h3 className="font-display text-2xl text-forest mb-2">Thank You!</h3>
        <p className="text-muted-foreground">
          We&rsquo;ve received your enquiry. Our team will reach out shortly.
        </p>
      </div>
    );
  }

  const inputCls = "bg-cream/60 border-forest/15 focus-visible:ring-gold";
  const grid = compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`lead-form-${type}`}>
      <div className={`grid ${grid} gap-4`}>
        {fields.includes("name") && (
          <div className="space-y-1.5">
            <Label className="text-forest">Full Name</Label>
            <Input data-testid="lead-name" className={inputCls} value={form.name || ""} onChange={set("name")} placeholder="Your name" />
          </div>
        )}
        {fields.includes("phone") && (
          <div className="space-y-1.5">
            <Label className="text-forest">Phone *</Label>
            <Input data-testid="lead-phone" className={inputCls} value={form.phone || ""} onChange={set("phone")} placeholder="+91 ..." />
          </div>
        )}
        {fields.includes("email") && (
          <div className="space-y-1.5">
            <Label className="text-forest">Email</Label>
            <Input data-testid="lead-email" type="email" className={inputCls} value={form.email || ""} onChange={set("email")} placeholder="you@company.com" />
          </div>
        )}
        {fields.includes("company") && (
          <div className="space-y-1.5">
            <Label className="text-forest">Business / Company</Label>
            <Input data-testid="lead-company" className={inputCls} value={form.company || ""} onChange={set("company")} placeholder="Company name" />
          </div>
        )}
        {fields.includes("city") && (
          <div className="space-y-1.5">
            <Label className="text-forest">City</Label>
            <Input data-testid="lead-city" className={inputCls} value={form.city || ""} onChange={set("city")} placeholder="City" />
          </div>
        )}
        {fields.includes("business_type") && (
          <div className="space-y-1.5">
            <Label className="text-forest">Business Type</Label>
            <Select value={form.business_type || ""} onValueChange={set("business_type")}>
              <SelectTrigger data-testid="lead-business-type" className={inputCls}><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {fields.includes("product") && (
          <div className="space-y-1.5">
            <Label className="text-forest">Product of Interest</Label>
            <Select value={form.product || ""} onValueChange={set("product")}>
              <SelectTrigger data-testid="lead-product" className={inputCls}><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>
                {PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {fields.includes("quantity") && (
          <div className="space-y-1.5">
            <Label className="text-forest">Estimated Monthly Quantity</Label>
            <Input data-testid="lead-quantity" className={inputCls} value={form.quantity || ""} onChange={set("quantity")} placeholder="e.g. 100 kg / month" />
          </div>
        )}
      </div>
      {fields.includes("message") && (
        <div className="space-y-1.5">
          <Label className="text-forest">Message</Label>
          <Textarea data-testid="lead-message" className={inputCls} rows={4} value={form.message || ""} onChange={set("message")} placeholder="Tell us about your requirement..." />
        </div>
      )}
      <div className="space-y-3 rounded-xl border border-forest/10 bg-cream/30 p-3">
        <label className="flex items-start gap-3 text-sm text-forest cursor-pointer">
          <input
            type="checkbox"
            checked={privacyConsent}
            onChange={(e) => setPrivacyConsent(e.target.checked)}
            className="mt-1 h-4 w-4 accent-forest"
            aria-label="I agree to Leaf Alchemy processing my personal information for enquiry response"
            required
          />
          <span>
            I agree to Leaf Alchemy processing the personal information I provide in this form for the purpose of responding to my enquiry, in accordance with the <Link to="/privacy-policy" className="text-olive underline underline-offset-2">Privacy Policy</Link>.
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm text-forest cursor-pointer">
          <input
            type="checkbox"
            checked={marketingConsent}
            onChange={(e) => setMarketingConsent(e.target.checked)}
            className="mt-1 h-4 w-4 accent-forest"
            aria-label="I would like to receive Leaf Alchemy product updates, offers and newsletters"
          />
          <span>I would like to receive Leaf Alchemy product updates, offers and newsletters.</span>
        </label>
      </div>
      <Button
        type="submit"
        disabled={loading}
        data-testid="lead-submit"
        className="w-full rounded-full bg-forest hover:bg-olive text-cream h-12 text-base font-medium transition-colors"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : submitLabel}
      </Button>
    </form>
  );
}
